import { Injectable, Injector, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ExperienceThemeService } from '../experience/experience-theme.service';
import { isDesignTokensLabEnabled } from './design-tokens-lab.enabled';
import {
    DEFAULT_DESIGN_TOKENS_PROFILE_ID,
    validateDesignTokensProfile,
    type DesignTokensProfileValidationResult,
} from './design-tokens-profile.util';
import type { NestedThemeTokens } from './flatten-theme-tokens';
import type { ThemeAssets } from './theme.assets.defaults';

const STORAGE_KEY = 'simfinix_design_tokens_lab_v1';

export type DesignTokensLabProfile = {
    id: string;
    name: string;
    tokens: NestedThemeTokens;
    assets?: Partial<ThemeAssets> | Record<string, string>;
    createdAt: string;
};

type StoredState = {
    activeProfileId: string;
    profiles: DesignTokensLabProfile[];
};

@Injectable({
    providedIn: 'root',
})
export class ThemeProfileService {
    private readonly injector = inject(Injector);
    private readonly profilesSubject = new BehaviorSubject<DesignTokensLabProfile[]>([]);
    private readonly activeProfileIdSubject = new BehaviorSubject<string>(
        DEFAULT_DESIGN_TOKENS_PROFILE_ID,
    );

    readonly profiles$ = this.profilesSubject.asObservable();
    readonly activeProfileId$ = this.activeProfileIdSubject.asObservable();

    constructor() {
        if (isDesignTokensLabEnabled()) {
            this.hydrateFromStorage();
        }
    }

    get profiles(): DesignTokensLabProfile[] {
        return this.profilesSubject.value;
    }

    get activeProfileId(): string {
        return this.activeProfileIdSubject.value;
    }

    getActiveOverlay(): {
        tokens: NestedThemeTokens;
        assets?: Partial<ThemeAssets> | Record<string, string>;
    } | null {
        if (!isDesignTokensLabEnabled()) {
            return null;
        }
        const id = this.activeProfileId;
        if (!id || id === DEFAULT_DESIGN_TOKENS_PROFILE_ID) {
            return null;
        }
        const profile = this.profiles.find((item) => item.id === id);
        if (!profile) {
            return null;
        }
        return {
            tokens: profile.tokens,
            assets: profile.assets,
        };
    }

    setActiveProfileId(profileId: string): void {
        if (!isDesignTokensLabEnabled()) {
            return;
        }
        const next =
            profileId === DEFAULT_DESIGN_TOKENS_PROFILE_ID ||
            this.profiles.some((item) => item.id === profileId)
                ? profileId
                : DEFAULT_DESIGN_TOKENS_PROFILE_ID;
        this.activeProfileIdSubject.next(next);
        this.persist();
        this.reapplyTheme();
    }

    addProfileFromJson(
        raw: unknown,
        fallbackName?: string,
    ): DesignTokensProfileValidationResult {
        if (!isDesignTokensLabEnabled()) {
            return { ok: false, errors: ['Lab de tokens disponible solo en entorno de desarrollo.'] };
        }
        const validated = validateDesignTokensProfile(raw, fallbackName);
        if (!validated.ok) {
            return validated;
        }

        const profile: DesignTokensLabProfile = {
            id: `profile_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            name: validated.name,
            tokens: validated.tokens,
            assets: validated.assets,
            createdAt: new Date().toISOString(),
        };
        this.profilesSubject.next([...this.profiles, profile]);
        this.activeProfileIdSubject.next(profile.id);
        this.persist();
        this.reapplyTheme();
        return validated;
    }

    removeProfile(profileId: string): void {
        if (!isDesignTokensLabEnabled() || profileId === DEFAULT_DESIGN_TOKENS_PROFILE_ID) {
            return;
        }
        const next = this.profiles.filter((item) => item.id !== profileId);
        this.profilesSubject.next(next);
        if (this.activeProfileId === profileId) {
            this.activeProfileIdSubject.next(DEFAULT_DESIGN_TOKENS_PROFILE_ID);
        }
        this.persist();
        this.reapplyTheme();
    }

    private reapplyTheme(): void {
        // Lazy resolve avoids circular DI with ExperienceThemeService.
        this.injector.get(ExperienceThemeService).reapplyWithCurrentBase();
    }

    private hydrateFromStorage(): void {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return;
            }
            const parsed = JSON.parse(raw) as StoredState;
            const profiles = Array.isArray(parsed?.profiles)
                ? parsed.profiles.filter(
                      (item) =>
                          item &&
                          typeof item.id === 'string' &&
                          typeof item.name === 'string' &&
                          item.tokens &&
                          typeof item.tokens === 'object',
                  )
                : [];
            this.profilesSubject.next(profiles);
            const active =
                typeof parsed?.activeProfileId === 'string' &&
                (parsed.activeProfileId === DEFAULT_DESIGN_TOKENS_PROFILE_ID ||
                    profiles.some((item) => item.id === parsed.activeProfileId))
                    ? parsed.activeProfileId
                    : DEFAULT_DESIGN_TOKENS_PROFILE_ID;
            this.activeProfileIdSubject.next(active);
        } catch {
            localStorage.removeItem(STORAGE_KEY);
        }
    }

    private persist(): void {
        if (!isDesignTokensLabEnabled()) {
            return;
        }
        const state: StoredState = {
            activeProfileId: this.activeProfileId,
            profiles: this.profiles,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
}
